/**
 * Отбивка после отправки формы обратной связи.
 *
 * Тексты и время работы задаются ТОЛЬКО в панели (content type=settings,
 * code=formSuccessReply). Если в панели задан текст для рабочего и/или
 * нерабочего времени — показываем нужный вариант по времени Москвы (диапазон
 * тоже из панели, по умолчанию 09:00–21:00). Если текстов в панели нет —
 * показываем прежнее сообщение формы в новом дизайне с заголовком «Спасибо,».
 *
 * Здесь захардкожен только заголовок «Спасибо,» — самих текстовок в коде нет.
 *
 * Плейсхолдеры в тексте (задаются в панели):
 *   #NAME#  — имя клиента из формы (поддерживается и старый #ИМЯ#);
 *   #ID#    — номер заявки (поддерживается и старый №ХХХХХ / #НОМЕР#).
 */

export const REPLY_SETTINGS_SERVICE = "settings";
export const REPLY_SETTINGS_CODE = "formSuccessReply";

/** Единственная захардкоженная строка — заголовок отбивки. */
export const DEFAULT_TITLE = "Спасибо,";

/** Настройки по умолчанию (без текстовок): используются, если панель пуста. */
export const DEFAULT_REPLY_SETTINGS = {
  title: DEFAULT_TITLE,
  subtitle: "",
  workStart: "",
  workEnd: "",
  workingText: "",
  nonWorkingText: "",
};

/** Приводим настройку из панели к единому виду; заголовок дефолтит на «Спасибо,». */
export function normalizeReplySettings(raw) {
  const data = raw && typeof raw === "object" ? raw : {};
  const pick = (key) =>
    typeof data[key] === "string" && data[key].trim() !== ""
      ? data[key].trim()
      : "";
  return {
    title: pick("title") || DEFAULT_TITLE,
    subtitle: pick("subtitle"),
    workStart: pick("workStart"),
    workEnd: pick("workEnd"),
    workingText: pick("workingText"),
    nonWorkingText: pick("nonWorkingText"),
  };
}

/** "09:00" -> минуты от полуночи. Некорректное значение -> fallback. */
function parseHmToMinutes(value, fallback) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || "").trim());
  if (!match) return fallback;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return fallback;
  return hours * 60 + minutes;
}

/** Текущее время в минутах от полуночи по Москве. */
export function getMoscowMinutes(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Moscow",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  return hour * 60 + minute;
}

/** Сейчас рабочее время по Москве? Диапазон берётся из настройки. */
export function isMoscowWorkingHours(settings, now = new Date()) {
  const start = parseHmToMinutes(settings.workStart, 9 * 60);
  const end = parseHmToMinutes(settings.workEnd, 21 * 60);
  const current = getMoscowMinutes(now);
  return current >= start && current < end;
}

function capitalizeFirst(text) {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// Плейсхолдер имени: #NAME# (актуальный) или #ИМЯ# (старый).
const NAME_TOKEN = /#NAME#|#ИМЯ#/g;
// Плейсхолдер номера: #ID#, #НОМЕР# или ряд Х/X (старый №ХХХХХ).
const NUMBER_TOKEN = /#ID#|#НОМЕР#|[ХXхx]{3,}/g;

/** Подставляем имя клиента; если имени нет — аккуратно убираем плейсхолдер. */
function applyName(text, name) {
  const trimmed = String(name || "").trim();
  if (trimmed) {
    return text.replace(NAME_TOKEN, trimmed);
  }
  let result = text.replace(
    /Уважаем(?:ый|ая)\s+(?:#NAME#|#ИМЯ#)/gi,
    "Уважаемый клиент",
  );
  result = result.replace(/(?:#NAME#|#ИМЯ#)\s*,?\s*/g, "");
  return capitalizeFirst(result);
}

/** Подставляем номер заявки вместо плейсхолдера #ID# / #НОМЕР# / №ХХХХХ. */
function applyNumber(text, number) {
  const value = number == null || number === "" ? "" : String(number);
  if (!value) {
    return text.replace(
      new RegExp(`№?\\s*(?:${NUMBER_TOKEN.source})`, "g"),
      "",
    );
  }
  return text.replace(NUMBER_TOKEN, value);
}

/**
 * Собираем готовую отбивку.
 * Если в панели заданы тексты — берём вариант по времени МСК с подстановками.
 * Если текстов нет — показываем прежнее сообщение формы (`fallbackText`).
 * @param {object} settings — настройки из панели (могут быть пустыми).
 * @param {string} name — имя клиента.
 * @param {string|number} number — номер заявки.
 * @param {string} [fallbackText] — прежний текст формы (когда панель без текстовок).
 * @param {Date} [now] — текущее время (для тестов).
 * @returns {{ title: string, subtitle: string, body: string }}
 */
export function resolveSuccessReply({
  settings,
  name,
  number,
  fallbackText = "",
  now = new Date(),
}) {
  const normalized = normalizeReplySettings(settings);
  const hasCustomTexts = Boolean(
    normalized.workingText || normalized.nonWorkingText,
  );

  if (!hasCustomTexts) {
    return { title: normalized.title, subtitle: "", body: fallbackText };
  }

  const isWorking = isMoscowWorkingHours(normalized, now);
  const template =
    (isWorking ? normalized.workingText : normalized.nonWorkingText) ||
    fallbackText;
  const body = applyNumber(applyName(template, name), number);
  return { title: normalized.title, subtitle: normalized.subtitle, body };
}
